import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getSubscriptions } from "../../services/subscriptionService"
import {
  CalendarCheck,
  Plus,
  Download,
  Filter,
  Eye,
} from "lucide-react"
import { Card } from "../../components/ui/Card"
import { StatsCard } from "../../components/ui/StatsCard"
import { Button } from "../../components/ui/Button"
import { SearchInput } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { Badge } from "../../components/ui/Badge"
import { EmptyState } from "../../components/ui/EmptyState"
import { TableSkeleton } from "../../components/ui/Skeleton"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"
import { fr } from "../../locales/fr"
import { subscriptionStatusLabel } from "../../locales/fr"

export default function SubscriptionsList() {
  const navigate = useNavigate()
  const [subscriptions, setSubscriptions] = useState([])
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    filterSubscriptions()
  }, [subscriptions, search, statusFilter])

  const load = async () => {
    setLoading(true)
    try {
      const data = await getSubscriptions()
      setSubscriptions(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error("Failed to load subscriptions", e)
    }
    setLoading(false)
  }

  const filterSubscriptions = () => {
    let result = [...subscriptions]

    if (search) {
      result = result.filter(
        (s) =>
          s.member?.toLowerCase().includes(search.toLowerCase()) ||
          s.pack?.toLowerCase().includes(search.toLowerCase())
      )
    }

    if (statusFilter !== "all") {
      result = result.filter((s) => s.status === statusFilter)
    }

    setFilteredSubscriptions(result)
  }

  const activeCount = subscriptions.filter((s) => s.status === "active").length
  const expiredCount = subscriptions.filter((s) => s.status === "expired").length
  const totalRevenue = subscriptions.reduce((sum, s) => sum + (s.price || 0), 0)

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge variant="success">{fr.status.active}</Badge>
      case "expired":
        return <Badge variant="destructive">{fr.status.expired}</Badge>
      case "pending":
        return <Badge variant="warning">{fr.status.pending}</Badge>
      default:
        return <Badge variant="outline">{subscriptionStatusLabel(status)}</Badge>
    }
  }

  const pctActive =
    subscriptions.length > 0
      ? Math.round((activeCount / subscriptions.length) * 100)
      : 0

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.subscriptions.title}</h1>
          <p className="text-muted-foreground">{fr.subscriptions.subtitle}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {fr.common.export}
          </Button>
          <Button onClick={() => navigate("/subscriptions/new")}>
            <Plus className="mr-2 h-4 w-4" />
            {fr.subscriptions.new}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title={fr.subscriptions.stats.total}
          value={subscriptions.length}
          change={fr.subscriptions.stats.newThisMonth}
          changeType="positive"
          icon={CalendarCheck}
        />
        <StatsCard
          title={fr.subscriptions.stats.active}
          value={activeCount}
          change={`${pctActive} ${fr.dashboard.stats.percentOfTotal}`}
          changeType="positive"
          icon={CalendarCheck}
        />
        <StatsCard
          title={fr.subscriptions.stats.expired}
          value={expiredCount}
          change={fr.dashboard.stats.needsRenewal}
          changeType="negative"
          icon={CalendarCheck}
        />
        <StatsCard
          title={fr.subscriptions.stats.totalValue}
          value={`${totalRevenue.toLocaleString()} MAD`}
          change={fr.dashboard.stats.allTimeRevenue}
          changeType="neutral"
          icon={CalendarCheck}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              placeholder={fr.subscriptions.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-44"
            >
              <option value="all">{fr.status.all}</option>
              <option value="active">{fr.status.active}</option>
              <option value="expired">{fr.status.expired}</option>
              <option value="pending">{fr.status.pending}</option>
            </Select>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <TableSkeleton rows={8} columns={8} />
        ) : filteredSubscriptions.length === 0 ? (
          <EmptyState
            icon={CalendarCheck}
            title={fr.subscriptions.emptyTitle}
            description={
              search || statusFilter !== "all"
                ? fr.subscriptions.emptyFiltered
                : fr.subscriptions.emptyInitial
            }
            action={() => navigate("/subscriptions/new")}
            actionLabel={fr.subscriptions.new}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{fr.subscriptions.table.member}</TableHead>
                <TableHead>{fr.subscriptions.table.pack}</TableHead>
                <TableHead>{fr.subscriptions.table.plan}</TableHead>
                <TableHead>{fr.subscriptions.table.startDate}</TableHead>
                <TableHead>{fr.subscriptions.table.endDate}</TableHead>
                <TableHead>{fr.subscriptions.table.status}</TableHead>
                <TableHead>{fr.subscriptions.table.price}</TableHead>
                <TableHead>{fr.common.view}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubscriptions.map((subscription) => (
                <TableRow key={subscription.id}>
                  <TableCell>
                    <span className="font-medium text-foreground">
                      {subscription.member}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-foreground">{subscription.pack}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{subscription.plan}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{subscription.startDate}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{subscription.endDate}</span>
                  </TableCell>
                  <TableCell>{getStatusBadge(subscription.status)}</TableCell>
                  <TableCell>
                    <span className="font-semibold text-foreground">
                      {subscription.price} MAD
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                    >
                      <Eye className="mr-1 h-4 w-4" />
                      {fr.common.view}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {filteredSubscriptions.length > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-4">
            <p className="text-sm text-muted-foreground">
              {fr.pagination.subscriptions(filteredSubscriptions.length, subscriptions.length)}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                {fr.common.previous}
              </Button>
              <Button variant="outline" size="sm" disabled>
                {fr.common.next}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
